import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  forwardRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ScrollColumnComponent } from '../scroll-column/scroll-column.component';
import { TimeFormat, TimeValue } from './timepicker.types';
import { DEFAULT_CONFIG, TimepickerConfig } from './timepicker.config';

export type TimepickerTheme = 'light' | 'dark';

@Component({
  selector: 'ng-scroll-timepicker',
  standalone: true,
  imports: [CommonModule, ScrollColumnComponent],
  templateUrl: './ng-timepicker.component.html',
  styleUrls: ['./ng-timepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NgTimepickerComponent),
      multi: true,
    },
  ],
})
export class NgTimepickerComponent implements OnInit, OnChanges, ControlValueAccessor {
  /** '12h' shows am/pm column; '24h' shows 00–23 hours with no am/pm */
  @Input() format: TimeFormat = '12h';

  /** Initial time string. Accepted: "9:45 am", "09:45 am", "21:45" */
  @Input() value = '';

  /**
   * Built-in theme preset.
   * 'light' (default) — white background, dark text.
   * 'dark' — dark surface, light text.
   * For full control, override CSS custom properties instead:
   *   --tp-bg, --tp-selection-bg, --tp-text-color, --tp-separator-color,
   *   --tp-selection-radius, --tp-picker-radius, --tp-picker-padding
   */
  @Input() theme: TimepickerTheme = 'light';

  /**
   * Layout & typography configuration.
   * Partial — only specify the values you want to change.
   *
   * @example
   * [config]="{ itemHeight: 56, fontSizeSelected: 24, opacityNear: 0.5 }"
   */
  @Input() config: TimepickerConfig = {};

  /** Emits formatted time string on every change */
  @Output() timeChange = new EventEmitter<string>();

  hours12: string[] = [];
  hours24: string[] = [];
  minutes: string[] = [];
  periods: string[] = ['am', 'pm'];

  hourIndex = 0;
  minuteIndex = 0;
  periodIndex = 0;

  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.buildArrays();
    this.parseValue(this.value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['format'] && !changes['format'].firstChange) {
      this.buildArrays();
      this.parseValue(this.value);
    }
    if (changes['value'] && !changes['value'].firstChange) {
      this.parseValue(this.value);
    }
  }

  /** Merged config — DEFAULT_CONFIG + consumer overrides */
  get mergedConfig(): Required<TimepickerConfig> {
    return { ...DEFAULT_CONFIG, ...this.config };
  }

  /**
   * CSS variables injected onto the root element.
   * --tp-item-height drives the selection-box height and separator line-height
   * without requiring the SCSS to know the runtime value.
   */
  get rootVars(): Record<string, string> {
    return { '--tp-item-height': `${this.mergedConfig.itemHeight}px` };
  }

  get hourItems(): string[] {
    return this.format === '12h' ? this.hours12 : this.hours24;
  }

  // ── ControlValueAccessor ─────────────────────────────────────────────────

  writeValue(val: string): void {
    if (val != null) {
      this.parseValue(val);
      this.cdr.markForCheck();
    }
  }

  registerOnChange(fn: (v: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }

  // ── Handlers ─────────────────────────────────────────────────────────────

  onHourChange(index: number): void { this.hourIndex = index; this.emit(); }
  onMinuteChange(index: number): void { this.minuteIndex = index; this.emit(); }
  onPeriodChange(index: number): void { this.periodIndex = index; this.emit(); }

  // ── Internals ─────────────────────────────────────────────────────────────

  private buildArrays(): void {
    this.hours12 = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
    this.hours24 = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
    this.minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
  }

  private parseValue(val: string): void {
    if (!val) { this.setDefaults(); return; }

    const normalized = val.trim().toLowerCase();
    const match = normalized.match(/^(\d{1,2}):(\d{2})(?:\s*(am|pm))?$/);
    if (!match) { this.setDefaults(); return; }

    let h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    const period = match[3] as 'am' | 'pm' | undefined;

    if (this.format === '12h') {
      if (period) {
        this.periodIndex = period === 'am' ? 0 : 1;
      } else {
        if (h >= 12) { this.periodIndex = 1; if (h > 12) h -= 12; }
        else { this.periodIndex = 0; if (h === 0) h = 12; }
      }
      this.hourIndex = Math.max(0, Math.min((h - 1 + 12) % 12, 11));
    } else {
      if (period === 'pm' && h < 12) h += 12;
      else if (period === 'am' && h === 12) h = 0;
      this.hourIndex = Math.max(0, Math.min(h, 23));
    }

    this.minuteIndex = Math.max(0, Math.min(m, 59));
    this.cdr.markForCheck();
  }

  private setDefaults(): void {
    this.hourIndex = this.format === '12h' ? 8 : 9;
    this.minuteIndex = 0;
    this.periodIndex = 0;
  }

  private emit(): void {
    const time = this.buildTimeString();
    this.timeChange.emit(time);
    this.onChange(time);
  }

  buildTimeString(): string {
    const h = this.hourItems[this.hourIndex];
    const m = this.minutes[this.minuteIndex];
    if (this.format === '12h') return `${h}:${m} ${this.periods[this.periodIndex]}`;
    return `${h}:${m}`;
  }
}
